// Автоимя live-записи: локальная модель называет тему по первой трети расшифровки,
// тема встаёт вместо «Запись с микрофона» перед той же датой.
//
// Здесь только чистые функции — сеть в services/titleService.js. Модель отвечает
// свободным текстом, поэтому всё, что она вернула, проходит через sanitizeTitle:
// в заголовок истории попадает либо короткое имя, либо ничего (остаётся имя-дата).
import { MIC_SOURCE_NAME } from './streamingHistory';
import { PASTED_SOURCE_NAME } from './manualText';

// Меньше 40 слов — первая треть это обрывок фразы, тему по ней не назвать.
const MIN_WORDS = 40;
// Потолок на вход: у двухчасовой записи треть — десятки тысяч знаков, модели столько не нужно.
const MAX_INPUT_CHARS = 6000;
// Заголовок длиннее — значит модель написала фразу вместо имени.
const MAX_TITLE_CHARS = 80;

/**
 * Кусок текста для модели или null, если текста слишком мало.
 *
 * По умолчанию это первая треть: у живой записи остальное ещё дописывается.
 * `whole` — для готового текста (вставка из буфера, файл), который целиком на руках:
 * треть от него была бы обрезанием без причины.
 */
export function pickTitleInput(
  transcript,
  { minWords = MIN_WORDS, maxChars = MAX_INPUT_CHARS, whole = false } = {},
) {
  const words = String(transcript || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < minWords) return null;

  const take = whole ? words.length : Math.floor(words.length / 3);
  const text = words.slice(0, take).join(' ');
  if (text.length <= maxChars) return text;

  const cut = text.slice(0, maxChars);
  const edge = cut.lastIndexOf(' ');            // не рвём слово посередине
  return edge > 0 ? cut.slice(0, edge) : cut;
}

/** Имя из ответа модели или null, если это не имя. */
export function sanitizeTitle(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return null;
  if (/[\r\n]/.test(text)) return null;         // несколько строк — это рассуждение, а не имя

  const clean = text
    .replace(/^[«"'`\s]+/, '')
    .replace(/[».,;:!?"'`\s]+$/, '')
    .trim();

  if (!clean || clean.length > MAX_TITLE_CHARS) return null;
  return clean;
}

/**
 * Наше ли это имя, то есть можно ли его заменить темой.
 *
 * Наши — только автоматические: «Запись с микрофона …» и пометка вставленного текста.
 * Имя файла и ручное переименование — чужие. Проверяется ДО обращения к модели:
 * иначе на каждую такую запись впустую уходит задача на GPU.
 */
export function canRename(previousTitle) {
  const prev = String(previousTitle || '');
  return prev === PASTED_SOURCE_NAME || prev.startsWith(`${MIC_SOURCE_NAME} `);
}

/**
 * Итоговый заголовок или null, если менять нечего.
 *
 * Живая запись названа «Запись с микрофона ДД.ММ.ГГ ЧЧ-ММ» — тема встаёт перед той
 * же датой. Вставленный из буфера текст названия не имеет вовсе, только пометку
 * источника, и получает одну тему: дату записи сайдбар и так показывает подписью.
 *
 * null, когда заголовок уже не наш — имя файла или ручное переименование: чужое
 * имя затирать не вправе.
 */
export function composeTitle(topic, previousTitle) {
  const name = sanitizeTitle(topic);
  if (!name) return null;

  const prev = String(previousTitle || '');
  if (prev === PASTED_SOURCE_NAME) return name;

  const prefix = `${MIC_SOURCE_NAME} `;
  if (!prev.startsWith(prefix)) return null;

  const stamp = prev.slice(prefix.length).trim();
  if (!stamp) return null;

  return `${name} · ${stamp}`;
}
